import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Typography,
  Button,
} from "@mui/material";
import { TParamsObrigatorios, IDetalheUsuarioPerfil, TParamExtras } from "../../../../types/api/configuracoes/detalhar-usuario";
import { formatarValorMonetario } from "../../../../helpers/masks";
import { PUBLICO_LABELS, PARAM_LABELS } from "./types-publico-permitido";

interface PropsDialogDadosExtras {
  open: boolean;
  title: string;
  perfil: IDetalheUsuarioPerfil | null;
  onClose: () => void;
}

export default function DialogDadosExtras({
  open,
  title,
  perfil,
  onClose,
}: PropsDialogDadosExtras) {

  const getParamValue = (perfil: IDetalheUsuarioPerfil, paramObrigatorio: string) => {
    const paramMap: Record<string, { acesso: TParamAcesso; value: any }> = {
      loja_id: { acesso: "loja", value: perfil.loja },
      vendedor_id: { acesso: "vendedor", value: perfil.vendedor },
      condicoes_pagamento_blocked_ids: { 
        acesso: "condicoes_pagamento_blocked", 
        value: perfil.condicoes_pagamento_blocked 
      },
      limite_alcada: { acesso: "limite_alcada", value: perfil.limite_alcada },
      publicos_permitidos: { acesso: "tipo_publico", value: perfil.tipo_publico },
    };

    const paramConfig = paramMap[paramObrigatorio] || { acesso: "loja", value: null };
    
    return { 
      paramAcesso: paramConfig.acesso,
      paramValue: paramConfig.value 
    };
  };

  const extractParamValues = (perfil: IDetalheUsuarioPerfil) => {
  if (!perfil.parametros_obrigatorios?.length) {
    return [];
  }

  const allValues: { value: any; paramType: string; uniqueKey: string }[] = [];
  
  perfil.parametros_obrigatorios.forEach(paramObrigatorio => {
    const { paramValue } = getParamValue(perfil, paramObrigatorio);
    
    if (Array.isArray(paramValue)) {
      paramValue.forEach((value, index) => {
        const uniqueKey = `${paramObrigatorio}-${JSON.stringify(value)}-${index}`;
        allValues.push({ 
          value, 
          paramType: paramObrigatorio,
          uniqueKey 
        });
      });
    } else if (paramValue) {
      const uniqueKey = `${paramObrigatorio}-${JSON.stringify(paramValue)}`;
      allValues.push({ 
        value: paramValue, 
        paramType: paramObrigatorio,
        uniqueKey 
      });
    }
  });

  return allValues;
};

const renderChip = (
  item: string | number | TParamExtras | null, 
  paramObrigatorio: TParamsObrigatorios
) => {
  if (!item) return null;

  const title = PARAM_LABELS[paramObrigatorio];
  
  if (paramObrigatorio === "publicos_permitidos") {
    const publicoLabel = PUBLICO_LABELS[item as string];
    if (publicoLabel) {
      return <Chip label={`${title}: ${publicoLabel}`} />;
    }
  }

  if (typeof item === "number") {
    return <Chip label={`${title}: ${formatarValorMonetario(item)}`} />;
  }

  if (typeof item === "string") {
    return <Chip label={`${title}: ${item}`} />;
  }

  return (
    <Chip 
      label={`${title}: ${item.id} - ${item.nome}`}
    />
  );
};

const renderParams = () => {
  if (!perfil) {
    return (
      <Typography color="textSecondary">
        Nenhum perfil selecionado
      </Typography>
    );
  }

  const allValues = extractParamValues(perfil);

  if (allValues.length === 0) {
    return (
      <Typography color="textSecondary">
        Nenhum parâmetro configurado para este perfil
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {allValues.map((item) => (
        <Box key={item.uniqueKey}>
          {renderChip(item.value, item.paramType as TParamsObrigatorios)}
        </Box>
      ))}
    </Box>
  );
};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
    >
      <DialogTitle>
        {title}
      </DialogTitle>
      
      <DialogContent>
        {renderParams()}
      </DialogContent>
      
      <DialogActions>
        <Button 
          variant="outlined"
          onClick={onClose}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type TParamAcesso = "loja" | "vendedor" | "condicoes_pagamento_blocked" | "limite_alcada" | "tipo_publico";